/**
 * CameraPreview (Welle 6a)
 *
 * Zwei Komponenten:
 *   - <SelfPreviewModal stream={...}> für den InCall-Fall: zeigt einen
 *     bereits vorhandenen Stream (typisch rtc.localPortraitStream) in groß
 *   - <StandaloneCameraCheckModal>: für den Pre-Call-Fall im Dashboard,
 *     startet selbst getUserMedia + portrait-pipeline und räumt beim Schließen auf.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortraitVideoTrack, PortraitPipeline } from '@/hooks/portraitTrack';
import { apiFetch } from '@/lib/auth';
import {
  listDevices, getPreferredDevice, setPreferredDevice, describeMediaError,
  getPreferredResolutionKey, setPreferredResolutionKey, RESOLUTION_OPTIONS, getPreferredResolution,
  getPreferredVolume, setPreferredVolume, speakerSelectionSupported,
  type DeviceKind, type DeviceOption, type ResolutionKey,
} from '@/lib/mediaDevices';
import {
  getBgConfig, setBgConfig, getCustomBg, setCustomBg, fileToScaledDataUrl,
  getCamFlip, setCamFlip,
  BLUR_LEVELS, PRESET_BACKGROUNDS, type BgConfig,
} from '@/lib/virtualBackground';

interface ModalShellProps {
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  status?: string;
}

function ModalShell({ onClose, title, children, status }: ModalShellProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16, padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        }}
      >
        {title && <div style={{ color: '#fff', fontSize: 14, opacity: 0.8 }}>{title}</div>}
        {children}
        {status && <div style={{ color: '#fff', fontSize: 12, opacity: 0.55 }}>{status}</div>}
        <button
          onClick={onClose}
          style={{
            marginTop: 8, padding: '8px 18px', background: 'transparent',
            color: '#fff', border: '1px solid #fff', borderRadius: 4,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          Schließen (Esc)
        </button>
      </div>
    </div>
  );
}

export function SelfPreviewModal({ stream, onClose }: { stream: MediaStream | null; onClose: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);

  if (!stream) return null;

  const videoTrack = stream.getVideoTracks()[0];
  const s = videoTrack?.getSettings();
  const dims = s ? `${s.width}×${s.height}` : '';

  return (
    <ModalShell
      onClose={onClose}
      title="Das sieht die Stele"
      status={dims ? `Output ${dims}` : undefined}
    >
      <video
        ref={ref}
        autoPlay
        playsInline
        muted
        style={{
          maxHeight: '80vh',
          maxWidth: '50vw',
          borderRadius: 8,
          background: '#000',
        }}
      />
    </ModalShell>
  );
}

/* ---- pre85b: kurze, freundliche Melodie als WAV-Data-URI (Ausgabe-/Lautstärketest) ---- */
function melodyDataUri(): string {
  const rate = 11025;
  // C5, E5, G5, C6 — kleines aufsteigendes Arpeggio
  const notes = [523.25, 659.25, 783.99, 1046.5];
  const noteDur = 0.16, gap = 0.02;
  const per = Math.floor(rate * (noteDur + gap));
  const n = per * notes.length;
  const buf = new Uint8Array(44 + n);
  const dv = new DataView(buf.buffer);
  const wr = (o: number, s: string) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
  wr(0, 'RIFF'); dv.setUint32(4, 36 + n, true); wr(8, 'WAVE'); wr(12, 'fmt ');
  dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, rate, true); dv.setUint32(28, rate, true); dv.setUint16(32, 1, true);
  dv.setUint16(34, 8, true); wr(36, 'data'); dv.setUint32(40, n, true);
  const sustain = Math.floor(rate * noteDur);
  for (let ni = 0; ni < notes.length; ni++) {
    const f = notes[ni];
    for (let i = 0; i < per; i++) {
      let amp = 0;
      if (i < sustain) {
        const env = Math.min(1, i / 300) * Math.pow(1 - i / sustain, 1.4); // weicher Attack + Decay
        amp = Math.sin((2 * Math.PI * f * i) / rate) * 95 * env;
      }
      buf[44 + ni * per + i] = 128 + Math.round(amp);
    }
  }
  let bin = ''; for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
  return 'data:audio/wav;base64,' + btoa(bin);
}

// pre155: theme-fähig (Tokens statt fester Dunkel-Hex), damit das Panel auch im
// Light-Mode und eingebettet in den Einstellungen korrekt aussieht.
const lbl: React.CSSProperties = { color: 'var(--muted)', fontSize: 11.5, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' };
const sel: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, background: 'var(--bg-elev-2)', color: 'var(--fg)', border: '1px solid var(--hairline)', fontSize: 13, fontFamily: 'inherit' };
const sectionBox: React.CSSProperties = { background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 };
const sectionHd: React.CSSProperties = { color: 'var(--fg)', fontSize: 13.5, fontWeight: 700 };
const testBtn: React.CSSProperties = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--bg-elev-3)', color: 'var(--fg)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const chip: React.CSSProperties = { padding: '6px 11px', borderRadius: 7, border: '1px solid var(--hairline)', background: 'var(--bg-elev-2)', color: 'var(--fg-soft)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const chipOn: React.CSSProperties = { background: 'var(--tenant-accent, #E2001A)', color: '#fff', borderColor: 'transparent' };
const thumb: React.CSSProperties = { width: 48, height: 48, borderRadius: 8, backgroundSize: 'cover', backgroundPosition: 'center', cursor: 'pointer', border: 'none', padding: 0 };

/**
 * pre155: DeviceCheckPanel — eingebettete, theme-fähige Variante des früheren
 * StandaloneCameraCheckModal. Rendert nur den Inhalt (Vorschau + Steuerung) ohne
 * eigenes Overlay, damit es als Tab in den Einstellungen leben kann. `onClose` ist
 * optional (z. B. wenn ein umgebendes Modal das Schließen übernimmt).
 */
export function DeviceCheckPanel({ onClose }: { onClose?: () => void }) {
  const [devices, setDevices] = useState<Record<DeviceKind, DeviceOption[]>>({ videoinput: [], audioinput: [], audiooutput: [] });
  const [camId, setCamId] = useState<string | null>(getPreferredDevice('videoinput'));
  const [micId, setMicId] = useState<string | null>(getPreferredDevice('audioinput'));
  const [spkId, setSpkId] = useState<string | null>(getPreferredDevice('audiooutput'));
  const [resKey, setResKey] = useState<ResolutionKey>(getPreferredResolutionKey());
  const [volume, setVolume] = useState<number>(getPreferredVolume());
  const [bg, setBg] = useState<BgConfig>(getBgConfig());
  const [customBg, setCustomBgState] = useState<string | null>(getCustomBg());
  const [tenantBgs, setTenantBgs] = useState<{ id: string; name: string; dataUrl: string }[]>([]);
  const [flip, setFlip] = useState<boolean>(getCamFlip());
  const [meshFollow, setMeshFollow] = useState(() => typeof localStorage !== 'undefined' && localStorage.getItem('bamoa.swap.followMesh') === '1');
  const [error, setError] = useState<string | null>(null);
  const [camDims, setCamDims] = useState<string>('');
  const [micLevel, setMicLevel] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordUrl, setRecordUrl] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const testAudioRef = useRef<HTMLAudioElement>(null);
  const pipelineRef = useRef<PortraitPipeline | null>(null);
  const rawVideoRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const speakerSupported = speakerSelectionSupported();

  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Initiale Berechtigung + Geräteliste (Labels erst nach Permission gefüllt)
  useEffect(() => {
    (async () => {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        tmp.getTracks().forEach((t) => t.stop());
      } catch (e) { setError(describeMediaError(e)); }
      try { setDevices(await listDevices()); } catch { /* */ }
    })();
  }, []);

  // pre85c: Mandanten-Hintergrundvorlagen laden
  useEffect(() => {
    let alive = true;
    apiFetch('/api/backgrounds')
      .then((r) => (r.ok ? r.json() : []))
      .then((bgs) => { if (alive && Array.isArray(bgs)) setTenantBgs(bgs); })
      .catch(() => { /* */ });
    return () => { alive = false; };
  }, []);

  // Kamera-Vorschau (Portrait-Pipeline) — Neustart bei Kamera/Auflösung
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { pipelineRef.current?.stop(); } catch { /* */ }
      rawVideoRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* */ } });
      const res = getPreferredResolution();
      try {
        const raw = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { ...(camId ? { deviceId: { exact: camId } } : {}), width: { ideal: res.width }, height: { ideal: res.height }, frameRate: { ideal: 30 } },
        });
        if (cancelled) { raw.getTracks().forEach((t) => t.stop()); return; }
        rawVideoRef.current = raw;
        const vt = raw.getVideoTracks()[0]?.getSettings();
        setCamDims(vt ? `${vt.width}×${vt.height}` : '');
        const pipeline = createPortraitVideoTrack(raw, { shortSide: 720, landscapeLongSide: 1280, fps: 30 });
        if (pipeline) { pipelineRef.current = pipeline; if (videoRef.current) videoRef.current.srcObject = pipeline.outputStream; }
        setError(null);
      } catch (e) { if (!cancelled) setError(describeMediaError(e)); }
    })();
    return () => { cancelled = true; };
  }, [camId, resKey]);

  // Mikrofon-Pegel — Neustart bei Mikrofon
  useEffect(() => {
    let cancelled = false;
    (async () => {
      micStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* */ } });
      try {
        const ms = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: { exact: micId } } : true });
        if (cancelled) { ms.getTracks().forEach((t) => t.stop()); return; }
        micStreamRef.current = ms;
        const ctx = audioCtxRef.current ?? new AudioContext();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(ms);
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        src.connect(an);
        const data = new Uint8Array(an.fftSize);
        const tick = () => {
          an.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
          setMicLevel(Math.min(1, Math.sqrt(sum / data.length) * 3));
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) { if (!cancelled) setError(describeMediaError(e)); }
    })();
    return () => { cancelled = true; if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [micId]);

  // Aufräumen beim Schließen
  useEffect(() => () => {
    try { pipelineRef.current?.stop(); } catch { /* */ }
    rawVideoRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* */ } });
    micStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* */ } });
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { audioCtxRef.current?.close(); } catch { /* */ }
    if (recordUrl) URL.revokeObjectURL(recordUrl);
  }, [recordUrl]);

  function pick(kind: DeviceKind, id: string) {
    setPreferredDevice(kind, id);
    if (kind === 'videoinput') setCamId(id);
    else if (kind === 'audioinput') setMicId(id);
    else setSpkId(id);
  }
  function pickRes(k: ResolutionKey) { setPreferredResolutionKey(k); setResKey(k); }
  function onVolume(v: number) { setVolume(v); setPreferredVolume(v); }

  function applyBg(cfg: BgConfig) { setBg(cfg); setBgConfig(cfg); }
  async function onCustomFile(file: File) {
    try {
      const dataUrl = await fileToScaledDataUrl(file);
      setCustomBg(dataUrl);
      setCustomBgState(dataUrl);
      applyBg({ mode: 'image', imageSrc: dataUrl });
    } catch { setError('Bild konnte nicht geladen werden.'); }
  }

  async function applySinkAndVolume(el: HTMLAudioElement) {
    el.volume = volume;
    if (speakerSupported && spkId) { try { await (el as any).setSinkId(spkId); } catch { /* */ } }
  }

  async function playTone() {
    const el = testAudioRef.current; if (!el) return;
    el.src = melodyDataUri();
    await applySinkAndVolume(el);
    try { await el.play(); } catch { /* */ }
  }

  function startRecording() {
    const ms = micStreamRef.current; if (!ms) return;
    try {
      chunksRef.current = [];
      const rec = new MediaRecorder(ms);
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (recordUrl) URL.revokeObjectURL(recordUrl);
        setRecordUrl(URL.createObjectURL(blob));
        setRecording(false);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setTimeout(() => { try { rec.state !== 'inactive' && rec.stop(); } catch { /* */ } }, 3500);
    } catch (e) { setError(describeMediaError(e)); }
  }
  function stopRecording() { try { recorderRef.current?.stop(); } catch { /* */ } }
  async function playRecording() {
    const el = testAudioRef.current; if (!el || !recordUrl) return;
    el.src = recordUrl;
    await applySinkAndVolume(el);
    try { await el.play(); } catch { /* */ }
  }

  return (
    <div style={{ display: 'flex', gap: 18, width: '100%' }}>
        {/* Vorschau links */}
        <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
          <div style={{ color: 'var(--fg-soft)', fontSize: 13, opacity: 0.85 }}>Das sieht die Stele</div>
          <video ref={videoRef} autoPlay playsInline muted style={{ height: '58vh', maxHeight: 520, maxWidth: '34vw', borderRadius: 10, background: '#000' }} />
          <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>{camDims ? `Aufnahme ${camDims}` : 'Kamera startet…'}</div>
        </div>

        {/* Steuerung rechts */}
        <div style={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, lineHeight: 1.4 }}>{error}</div>}

          {/* Kamera */}
          <div style={sectionBox}>
            <div style={sectionHd}>Kamera</div>
            <div>
              <label style={lbl}>Quelle</label>
              <select style={sel} value={camId ?? ''} onChange={(e) => pick('videoinput', e.target.value)}>
                {!camId && <option value="">Standard</option>}
                {devices.videoinput.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Auflösung</label>
              <select style={sel} value={resKey} onChange={(e) => pickRes(e.target.value as ResolutionKey)}>
                {RESOLUTION_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Bild horizontal spiegeln</label>
              <button onClick={() => { const v = !flip; setFlip(v); setCamFlip(v); }}
                style={{ ...chip, ...(flip ? chipOn : {}) }}>{flip ? 'An' : 'Aus'}</button>
            </div>
            {/* pre255: Kamerawahl-Mitnahme fuer per Link zugeschaltete dritte Teilnehmer */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>
                Dritte sehen meine Kameraauswahl
                <div style={{ fontSize: 10.5, color: 'var(--muted)', fontWeight: 400, marginTop: 2 }}>
                  Beim Kamerawechsel an der Stele (Klick aufs kleine Bild) folgt auch das Bild für
                  per Link zugeschaltete Teilnehmer. Aus: Dritte behalten das Porträt-Bild.
                </div>
              </label>
              <button onClick={() => { const v = localStorage.getItem('bamoa.swap.followMesh') !== '1'; localStorage.setItem('bamoa.swap.followMesh', v ? '1' : '0'); setMeshFollow(v); }}
                style={{ ...chip, ...(meshFollow ? chipOn : {}) }}>{meshFollow ? 'An' : 'Aus'}</button>
            </div>
            <div>
              <label style={lbl}>Virtueller Hintergrund</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {([['none', 'Aus'], ['blur', 'Weichzeichner'], ['image', 'Bild']] as const).map(([m, label]) => (
                  <button key={m}
                    onClick={() => applyBg(m === 'none' ? { mode: 'none' }
                      : m === 'blur' ? { mode: 'blur', blur: bg.blur ?? 12 }
                      : { mode: 'image', imageSrc: bg.imageSrc ?? customBg ?? PRESET_BACKGROUNDS[0].src })}
                    style={{ ...chip, ...(bg.mode === m ? chipOn : {}) }}>{label}</button>
                ))}
              </div>
              {bg.mode === 'blur' && (
                <div style={{ display: 'flex', gap: 6 }}>
                  {BLUR_LEVELS.map((b) => (
                    <button key={b.key} onClick={() => applyBg({ mode: 'blur', blur: b.px })}
                      style={{ ...chip, ...(bg.blur === b.px ? chipOn : {}) }}>{b.label}</button>
                  ))}
                </div>
              )}
              {bg.mode === 'image' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {tenantBgs.map((p) => (
                    <button key={p.id} title={p.name} onClick={() => applyBg({ mode: 'image', imageSrc: p.dataUrl })}
                      style={{ ...thumb, backgroundImage: `url(${p.dataUrl})`, outline: bg.imageSrc === p.dataUrl ? '2px solid var(--tenant-accent, #E2001A)' : '1px solid #2a5', boxShadow: '0 0 0 1px #2a5 inset' }} />
                  ))}
                  {PRESET_BACKGROUNDS.map((p) => (
                    <button key={p.id} title={p.label} onClick={() => applyBg({ mode: 'image', imageSrc: p.src })}
                      style={{ ...thumb, backgroundImage: `url(${p.src})`, outline: bg.imageSrc === p.src ? '2px solid var(--tenant-accent, #E2001A)' : '1px solid #333' }} />
                  ))}
                  {customBg && (
                    <button title="Eigenes Bild" onClick={() => applyBg({ mode: 'image', imageSrc: customBg })}
                      style={{ ...thumb, backgroundImage: `url(${customBg})`, outline: bg.imageSrc === customBg ? '2px solid var(--tenant-accent, #E2001A)' : '1px solid #333' }} />
                  )}
                  <label style={{ ...thumb, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa', fontSize: 22, cursor: 'pointer', background: '#15171c' }}>
                    +
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) void onCustomFile(f); }} />
                  </label>
                </div>
              )}
              <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 6 }}>Wird auf deinem Rechner berechnet · gilt für Vorschau und Call</div>
            </div>
          </div>

          {/* Mikrofon */}
          <div style={sectionBox}>
            <div style={sectionHd}>Mikrofon</div>
            <div>
              <label style={lbl}>Quelle</label>
              <select style={sel} value={micId ?? ''} onChange={(e) => pick('audioinput', e.target.value)}>
                {!micId && <option value="">Standard</option>}
                {devices.audioinput.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Pegel</label>
              <div style={{ height: 10, borderRadius: 5, background: 'var(--bg-elev-3)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round(micLevel * 100)}%`, background: micLevel > 0.7 ? '#e0b04a' : '#4ea36a', transition: 'width 80ms linear' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!recording
                ? <button style={testBtn} onClick={startRecording}>● 3 s aufnehmen</button>
                : <button style={{ ...testBtn, color: '#e0796a' }} onClick={stopRecording}>■ Stopp</button>}
              <button style={{ ...testBtn, opacity: recordUrl ? 1 : 0.5 }} disabled={!recordUrl} onClick={playRecording}>▶ Wiedergabe</button>
            </div>
          </div>

          {/* Wiedergabe */}
          <div style={sectionBox}>
            <div style={sectionHd}>Wiedergabe</div>
            <div>
              <label style={lbl}>Ausgabe{!speakerSupported && ' (Auswahl in diesem Browser nicht verfügbar)'}</label>
              <select style={sel} value={spkId ?? ''} disabled={!speakerSupported} onChange={(e) => pick('audiooutput', e.target.value)}>
                {!spkId && <option value="">Standard</option>}
                {devices.audiooutput.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Lautstärke · {Math.round(volume * 100)}%</label>
              <input type="range" min={0} max={100} value={Math.round(volume * 100)} onChange={(e) => onVolume(Number(e.target.value) / 100)} style={{ width: '100%' }} />
            </div>
            <button style={testBtn} onClick={playTone}>♪ Melodie abspielen</button>
          </div>

          {onClose && (
            <button onClick={onClose} style={{ marginTop: 4, padding: '9px 16px', background: 'transparent', color: 'var(--fg)', border: '1px solid var(--border)', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Fertig (Esc)</button>
          )}
        </div>
      <audio ref={testAudioRef} style={{ display: 'none' }} />
    </div>
  );
}
